import React, { useState, useEffect } from 'react';
import { CheckCircle2, Heart, Flame, BookOpen, /* Coins, */ AudioLines, MessageCircle, BookImage, Video, Gift, Sparkles, Zap } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { trackCheckoutInitiation } from "@/services/meta-events";
import { Purchases, PurchasesOfferings } from '@revenuecat/purchases-capacitor';
import { isNativeApp } from '@/services/payment-controller';
import { useAnalytics } from "@/hooks/useAnalytics";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { Capacitor } from '@capacitor/core';

// The Capacitor SDK doesn't export a dedicated Package type, so we alias it to any for now
type RevenueCatPackage = any;

interface AdvancedFeature {
  text: string;
  tag?: string | null;
}

interface FeatureItem {
  icon: React.ReactNode;
  text: string;
  tag?: string | null;
  advancedListTitle?: string;
  advancedList?: AdvancedFeature[];
}

interface PlanPriceDetails {
  price: string;
  priceSuffix: string;
  originalPrice: string;
  discountPercentage: number;
  paymentLink?: string;
  rcPackage?: RevenueCatPackage;
  fullPriceText?: string;
}

interface PlanProps {
  title: string;
  tagline: string;
  icon: React.ReactNode;
  color: string;
  features: FeatureItem[];
  promoTag?: string;
  isPopular?: boolean;
  buttonText: string;
  monthlyData: PlanPriceDetails;
  annualData: PlanPriceDetails;
  billingPeriod: 'monthly' | 'annual';
}

// Helper function to render tags
const renderTag = (tag: string | null | undefined) => {
  if (!tag) return null;
  
  const tagColors: Record<string, string> = {
    'Em Breve': 'bg-blue-100 text-blue-700',
    '2026': 'bg-orange-100 text-orange-700',
    'AGO - 2025': 'bg-green-100 text-green-700'
  };
  
  const colorClass = tagColors[tag] || 'bg-gray-100 text-gray-700';
  
  return (
    <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${colorClass}`}>
      {tag}
    </span>
  );
};

// --- Plan Component (JSX Updated) ---
const Plan: React.FC<PlanProps> = ({ 
  title, 
  tagline,
  icon, 
  color, 
  features,
  promoTag,
  isPopular,
  buttonText,
  monthlyData,
  annualData,
  billingPeriod
}) => {
  const { user, isLoading: authLoading } = useAuth();
  const { isImpersonating } = useImpersonation();
  const { trackCheckoutInitiated } = useAnalytics();
  const currentPlanData = billingPeriod === 'annual' ? annualData : monthlyData;
  
  const handleClick = async () => {
    const currentPlanData = billingPeriod === 'monthly' ? monthlyData : annualData;
    const native = isNativeApp();
    const platform = Capacitor.getPlatform();

    console.log('🎯 BUTTON CLICK DEBUG:');
    console.log('   Plan:', title);
    console.log('   Billing Period:', billingPeriod);
    console.log('   Platform:', platform);
    console.log('   Is Native:', native);

    // --- Track checkout initiation ---
    let priceForTracking = 0;
    if (native && currentPlanData.rcPackage?.product?.price) {
      priceForTracking = currentPlanData.rcPackage.product.price;
    } else {
      priceForTracking = parseFloat(currentPlanData.price.replace(/[^0-9,.-]/g, '').replace(',', '.')) || 0;
    }
    
    // Ensure we have user email before tracking
    const userEmail = user?.email || '';
    const userId = user?.id || '';

    console.log('Tracking checkout with user data:', {
      email: userEmail ? `✓ present (${userEmail})` : '✗ missing',
      userId: userId ? `✓ present (${userId})` : '✗ missing'
    });
    
    // Track checkout initiation with user data if available
    if (userEmail && priceForTracking) {
      const planName = billingPeriod === 'monthly' 
        ? `${title} Monthly Plan` 
        : `${title} Annual Plan`;
      
      // Meta tracking (existing)
      if (billingPeriod === 'monthly') {
        await trackCheckoutInitiation(
          String(userEmail), 
          priceForTracking, 
          'BRL',
          `${title} Monthly Plan`,
          String(userId)
        );
      } else {
        await trackCheckoutInitiation(
          String(userEmail), 
          priceForTracking, 
          'BRL',
          `${title} Annual Plan`,
          String(userId)
        );
      }
      
      // 🎯 NEW: Mixpanel tracking (parallel to Meta)
      try {
        trackCheckoutInitiated(planName, priceForTracking, billingPeriod, 'BRL');
        console.log('✅ Mixpanel Checkout Initiated event tracked');
      } catch (mixpanelError) {
        console.error('Failed to track Mixpanel checkout event:', mixpanelError);
      }
    }

    // --- Purchase flow - Mobile uses ONLY RevenueCat, Web uses Stripe ---
    if (platform === 'ios' || platform === 'android') {
      // MOBILE: Only RevenueCat, no Stripe fallbacks
      if (currentPlanData.rcPackage) {
        try {
          console.log(`${platform.toUpperCase()}: Attempting RevenueCat purchase with package:`, currentPlanData.rcPackage);
          const result = await Purchases.purchasePackage({ aPackage: currentPlanData.rcPackage });
          console.log('Purchase successful:', result);
          
          if (result.customerInfo && result.customerInfo.entitlements) {
            console.log('Customer entitlements:', result.customerInfo.entitlements);
            // Refresh subscription status in your app
            window.location.reload();
          }
        } catch (e: any) {
          console.error('RevenueCat purchase error:', e);
          if (!e?.userCancelled) {
            alert('Erro ao processar compra. Tente novamente.');
          }
        }
      } else {
        console.error(`${platform.toUpperCase()}: No RevenueCat package available`);
        alert('Compra temporariamente indisponível. Por favor, atualize o aplicativo.');
      }
    } else {
      // WEB: Use Stripe payments
      if (currentPlanData.paymentLink) {
        console.log('WEB: Opening Stripe payment link');
        window.open(currentPlanData.paymentLink, '_blank');
      } else {
        alert('Erro: Link de pagamento não disponível.');
      }
    }
  };

  // Button color mapping
  const buttonColorClasses = {
    Flerte: "bg-purple-600 hover:bg-purple-700 text-white",
    Desejo: "bg-pink-600 hover:bg-pink-700 text-white",
    Atração: "bg-blue-600 hover:bg-blue-700 text-white",
    Paixão: "bg-red-600 hover:bg-red-700 text-white",
  };

  const getDisplayPrice = (planData: PlanPriceDetails): {
    price: string;
    priceSuffix?: string;
    secondaryInfo?: string;
  } => {
    const platform = Capacitor.getPlatform();

    // iOS nativo com pacote válido da RevenueCat
    if (platform === 'ios' && planData.rcPackage?.product?.priceString) {
      const product = planData.rcPackage.product;

      // Cálculo do valor médio mensal
      const monthlyEquivalent = product.priceAmountMicros
        ? (product.priceAmountMicros / 1_000_000 / 12).toLocaleString('pt-BR', {
          style: 'currency',
          currency: 'BRL',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
        : undefined;

      return {
        price: product.priceString, // Exibe valor anual exato do RevenueCat
        priceSuffix: billingPeriod === 'monthly' ? '/mensal' : '/anual',
        secondaryInfo:
          billingPeriod === 'annual' && monthlyEquivalent
            ? `equivalente a ${monthlyEquivalent}/mês`
            : undefined,
      };
    }

    // Fallback para Android e Web
    return {
      price: planData.price,
      priceSuffix: planData.priceSuffix,
      secondaryInfo:
        billingPeriod === 'annual' ? planData.fullPriceText : undefined,
    };
  };


  const { price, priceSuffix, secondaryInfo } = getDisplayPrice(currentPlanData);
  const showOriginalPrice = !(isNativeApp() && currentPlanData.rcPackage?.product?.priceString);


  const platform = Capacitor.getPlatform();
  const isIOS = platform === 'ios';

  const tagsToHideOnIOS = ['Em Breve', 'AGO - 2025'];

  const filteredFeatures = features
    .map((feature) => {
      // Oculta advancedList com tags específicas no iOS
      const cleanAdvancedList = feature.advancedList?.filter(
        adv => !(isIOS && adv.tag && tagsToHideOnIOS.includes(adv.tag))
      );

      return {
        ...feature,
        advancedList: cleanAdvancedList
      };
    })
    .filter((feature) => {
      if (isIOS) {
        // Oculta features com tags específicas
        if (feature.tag && tagsToHideOnIOS.includes(feature.tag)) return false;

        // Oculta features com ícone Coins (importado do lucide-react)
        if ((feature.icon as any)?.type?.name === 'Coins') return false;
      }

      // Oculta features sem texto ou lista útil
      return feature.text || (feature.advancedList?.length ?? 0) > 0;
    });


  return (
    <Card 
      className={`relative mb-6 overflow-hidden border-2 transition-all duration-300 ${color} ${isPopular ? 'border-yellow-400' : 'border-transparent'}`}
    >
      {/* --- Tags --- */}
      <div className="absolute top-3 right-3 flex flex-col items-end space-y-1 z-10">
        {isPopular && (
          <div className="bg-yellow-400 text-yellow-900 text-xs font-bold py-0.5 px-2 rounded-full shadow">
            Mais Popular
          </div>
        )}
        {promoTag && Capacitor.getPlatform() !== 'ios' && (
          <div className="bg-white/80 backdrop-blur-sm text-purple-800 text-xs font-bold py-0.5 px-2 rounded-full shadow">
            {promoTag}
          </div>
        )}

      </div>

      <div className="p-5">
        {/* --- Header --- */}
        <div className="flex items-center space-x-3 mb-1">
          <div className={`p-1.5 rounded-lg bg-white/20`}>{icon}</div>
          <h3 className="text-lg font-bold text-white">{title}</h3>
        </div>
        <p className="text-sm text-white/80 mb-4 min-h-[20px]">{tagline}</p> 
        
        {/* --- Updated Price Section --- */}
        <div className="mb-5 text-white min-h-[70px]">
          {/* Main Price Line */}
          <div className="flex flex-wrap items-baseline">
            <span className="text-3xl font-bold mr-1.5">{price}</span>
            <span className="text-sm text-white/80 mr-1.5">{priceSuffix}</span>
          </div>

          {secondaryInfo && (
            <p className="text-sm text-white/80 mt-1">{secondaryInfo}</p>
          )}

          {showOriginalPrice && (
            <p className="text-xs text-white/60 mt-1">
              <span className="line-through">{currentPlanData.originalPrice}</span> {billingPeriod === 'monthly' ? 'por mês' : 'por ano'}
            </p>
          )}
        </div>

        
        {/* --- Features --- */}
        <div className="space-y-3.5 mb-6 min-h-[150px]"> 
          {filteredFeatures.map((feature, index) => (
            <div key={index}>
              {/* Main Feature Item */} 
              <div className="flex items-start text-white text-sm">
                <div className="text-white/80 mr-2 mt-0.5">{feature.icon}</div> 
                <span>{feature.text}</span>
                {/* Render tag if directly on main feature (like Paixao video) */}
                {feature.tag && renderTag(feature.tag)} 
              </div>

              {/* Advanced List (Coins section) */} 
              {feature.advancedList && (
                <div className="mt-2 pl-7 text-sm">
                  <ul className="space-y-1.5">
                    {feature.advancedList.map((advFeature, advIndex) => (
                      <li key={advIndex} className="flex items-center text-white/90">
                        <CheckCircle2 className="h-4 w-4 mr-1.5 flex-shrink-0 text-green-400" />
                        <span>{advFeature.text}</span>
                        {renderTag(advFeature.tag)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* --- Button --- */}
        <Button 
          onClick={handleClick} 
          className={`w-full font-semibold text-base py-3 h-auto ${buttonColorClasses[title] || 'bg-gray-500 hover:bg-gray-600'}`} 
          size="lg"
        >
          {buttonText}
        </Button>
      </div>
    </Card>
  );
};

const SubscriptionPlans: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<"annual" | "monthly">("annual");
  
  // Helper function to conditionally include paymentLink (exclude on mobile, keep on web)
  const getPaymentLink = (stripeUrl: string) => {
    const platform = Capacitor.getPlatform();
    const isMobile = platform === 'ios' || platform === 'android';
    
    // Build-time check to completely exclude URLs from mobile builds
    if (import.meta.env.BUILD_TARGET === 'mobile' || isMobile) {
      return undefined; // No URL in mobile binary
    }
    return stripeUrl;
  };
  
  const isIOS = Capacitor.getPlatform() === 'ios';

  const initialPlans = {
    flerte: {
      title: "Flerte",
      tagline: "Eu só preciso de uma chance",
      icon: <Gift className="h-6 w-6 text-white" />,
      color: "bg-gradient-to-b from-purple-800 to-purple-900 border border-purple-700",
      promoTag: "35% OFF",
      isPopular: false,
      buttonText: "Quero flertar",
      features: [
        {
          icon: <BookOpen className="h-5 w-5"/>,
          text: "Histórias em Texto Ilimitadas"
        },
        // Só inclui o item das moedas se não for iOS
        // ...(!isIOS ? [{
        //   icon: <Coins className="h-5 w-5" />,
        //   text: "500 Moedas para desbloquear funcionalidades avançadas como:",
        //   advancedList: [
        //     { text: "Histórias narradas e sonorizadas", tag: "Em Breve" },
        //     { text: "Chat com personagem", tag: "Em Breve" },
        //     { text: "Fanfiction", tag: "AGO - 2025" }
        //   ]
        // }] : [])
      ],
      monthlyData: {
        price: "R$ 14,90",
        priceSuffix: "/mensal",
        originalPrice: "R$ 14,90",
        discountPercentage: 0, 
        paymentLink: getPaymentLink("https://buy.stripe.com/8wM15CaSRg4c6xG288")
      },
      annualData: {
        price: "R$ 12,49",
        priceSuffix: "por mês cobrados anualmente",
        originalPrice: "de R$ 149,90",
        fullPriceText: "R$ 149,90/anual",
        discountPercentage: 0, 
        paymentLink: getPaymentLink("https://buy.stripe.com/dR629Gd0Z3hqe08aEF")
      }
    },
    desejo: {
      title: "Desejo",
      tagline: "Você não vai se arrepender",
      icon: <Heart className="h-6 w-6 text-white" />,
      color: "bg-gradient-to-b from-pink-700 to-pink-900 border border-pink-600",
      promoTag: "35% OFF",
      isPopular: false,
      buttonText: "Quero desejar",
      features: [
        {
          icon: <BookOpen className="h-5 w-5"/>,
          text: "Histórias em Texto e Áudio Ilimitadas"
        },
        // Só inclui o item das moedas se não for iOS
        // ...(!isIOS ? [{
        //   icon: <Coins className="h-5 w-5"/>,
        //   text: "1000 Moedas para desbloquear funcionalidades avançadas como:",
        //   advancedList: [
        //     { text: "Áudio", tag: null },
        //     { text: "Histórias narradas e sonorizadas", tag: "Em Breve" },
        //     { text: "Chat com personagem", tag: "Em Breve" },
        //     { text: "Fanfiction", tag: "AGO - 2025" }
        //   ]
        // }] : [])
      ],
      monthlyData: {
        price: "R$ 24,90",
        priceSuffix: "/mensal",
        originalPrice: "R$ 24,90",
        discountPercentage: 0, 
        paymentLink: getPaymentLink("https://buy.stripe.com/28o4hObWVcS0f4c8wy")
      },
      annualData: {
        price: "R$ 20,83",
        priceSuffix: "por mês cobrados anualmente",
        originalPrice: "de R$ 249,90",
        fullPriceText: "R$ 249,90/anual",
        discountPercentage: 0, 
        paymentLink: getPaymentLink("https://buy.stripe.com/4gw3dK1ihg4c1dmdQT")
      }
    },
    atracao: {
      title: "Atração",
      tagline: "O magnetismo do desejo",
      icon: <Sparkles className="h-6 w-6 text-white" />,
      color: "bg-gradient-to-b from-blue-400 to-purple-600 border border-blue-500",
      promoTag: "NOVO",
      isPopular: false,
      buttonText: "Quero atrair",
      features: [
        {
          icon: <BookOpen className="h-5 w-5"/>,
          text: "Histórias em Texto e Áudio Ilimitadas"
        },
        {
          icon: <MessageCircle className="h-5 w-5"/>,
          text: "Chat com Personagens"
        },
        {
          icon: <Zap className="h-5 w-5"/>,
          text: "Twists Exclusivos (Fanfictions)"
        }
      ],
      monthlyData: {
        price: "R$ 39,90",
        priceSuffix: "/mensal",
        originalPrice: "R$ 39,90",
        discountPercentage: 0, 
        paymentLink: getPaymentLink("https://buy.stripe.com/fZu14hgiFdnP0ZRd3zgw006")
      },
      annualData: {
        price: "R$ 33,25",
        priceSuffix: "por mês cobrados anualmente",
        originalPrice: "de R$ 399,00",
        fullPriceText: "R$ 399,00/anual",
        discountPercentage: 0, 
        paymentLink: getPaymentLink("https://buy.stripe.com/eVq9ANgiF83v9wnd3zgw007")
      }
    },
    paixao: {
      title: "Paixão",
      tagline: "Vamos viver intensamente",
      icon: <Flame className="h-6 w-6 text-white" />,
      color: "bg-gradient-to-b from-red-700 to-red-900 border border-red-600",
      promoTag: "50% OFF",
      isPopular: true,
      buttonText: "Quero apaixonar",
      features: [
        {
          icon: <Video className="h-5 w-5"/>,
          text: "Multimídia: Histórias em Texto, Áudio e Vídeo Ilimitadas.",
          tag: "2026"
        },
        // Só inclui o item das moedas se não for iOS
        // ...(!isIOS ? [{
        //   icon: <Coins className="h-5 w-5"/>,
        //   text: "5000 Moedas para desbloquear funcionalidades avançadas como:",
        //   advancedList: [
        //     { text: "Histórias narradas e sonorizadas", tag: "Em Breve" },
        //     { text: "Chat com personagem", tag: "Em Breve" },
        //     { text: "Fanfiction", tag: "AGO - 2025" }
        //   ]
        // }] : [])
      ],
      monthlyData: {
        price: "R$ 49,90",
        priceSuffix: "/mensal",
        originalPrice: "R$ 49,90",
        discountPercentage: 0, 
        paymentLink: getPaymentLink("https://buy.stripe.com/14kcOk3qpdW44pyfZ5")
      },
      annualData: {
        price: "R$ 41,58",
        priceSuffix: "por mês cobrados anualmente",
        originalPrice: "de R$ 499,90",
        fullPriceText: "R$ 499,90/anual",
        discountPercentage: 0, 
        paymentLink: getPaymentLink("https://buy.stripe.com/3cs6pW3qp3hq4pyfZ4")
      }
    }
  };

  // State & RevenueCat sync
  const [plans, setPlans] = useState(initialPlans);
  const [isLoading, setIsLoading] = useState(isNativeApp());

  useEffect(() => {
    const syncOfferings = async () => {
      if (!isNativeApp()) {
        if (JSON.stringify(plans) !== JSON.stringify(initialPlans)) {
           setPlans(initialPlans);
        }
        return;
      }
      
      try {
        setIsLoading(true);
        const offerings: PurchasesOfferings = await Purchases.getOfferings();
        console.log('RevenueCat offerings received:', offerings);
        
        // Verificação mais segura para evitar erro React #31
        if (!offerings || !offerings.all) {
          console.warn('No offerings or offerings.all is null/undefined');
          setIsLoading(false);
          return;
        }

        // Verificação mais segura para extrair packages
        const packages = Object.values(offerings.all)
          .filter(offering => offering && offering.availablePackages) // Filtrar offerings válidas
          .flatMap(offering => offering.availablePackages || []); // Usar array vazio como fallback

        if (packages.length === 0) {
          console.warn('No packages found in any offering');
          setIsLoading(false);
          return;
        }

        console.log('Available packages:', packages);
        
        const updatedPlans: typeof initialPlans = Object.fromEntries(
          Object.entries(initialPlans).map(([key, plan]) => [
            key,
            {
              ...plan,                                         // ícones e demais props intactos
              monthlyData: { ...plan.monthlyData },           // novo objeto (podemos adicionar rcPackage)
              annualData : { ...plan.annualData  },           // idem
            },
          ])
        ) as typeof initialPlans;

        packages.forEach((pkg) => {
          // Verificação de segurança para o package
          if (!pkg || !pkg.product || !pkg.product.identifier) {
            console.warn('Invalid package found:', pkg);
            return;
          }

          const productIdentifier = pkg.product.identifier.toLowerCase();
          
          console.log('🔍 DETAILED DEBUG for:', pkg.product.identifier);
          console.log('   Original ID:', pkg.product.identifier);
          console.log('   Lowercase ID:', productIdentifier);
          console.log('   Package Type:', pkg.packageType);
          console.log('   Price String:', pkg.product.priceString);

          let planKey: keyof typeof updatedPlans | null = null;
          let periodKey: 'monthly' | 'annual' | null = null;

          // Determine Plan Type from productIdentifier
          if (productIdentifier.includes('flerte')) {
            planKey = 'flerte';
          } else if (productIdentifier.includes('desejo')) {
            planKey = 'desejo';
          } else if (productIdentifier.includes('atracao')) {
            planKey = 'atracao';
          } else if (productIdentifier.includes('paixao')) { 
            planKey = 'paixao';
          }

          // Determina o período com prioridade para o packageType
          if (pkg.packageType === 'ANNUAL') {
            periodKey = 'annual';
          } else if (pkg.packageType === 'MONTHLY') {
            periodKey = 'monthly';
          } else {
            if (
              productIdentifier.includes(':yearly') ||
              productIdentifier.includes('yearly') ||
              productIdentifier.includes('annual')
            ) {
              periodKey = 'annual';
            } else if (
              productIdentifier.includes(':monthly') ||
              productIdentifier.includes('monthly')
            ) {
              periodKey = 'monthly';
            }
          }

          console.log('   planKey result:', planKey);
          console.log('   periodKey result:', periodKey);

          if (planKey && periodKey && updatedPlans[planKey]) {
            const targetPlan = updatedPlans[planKey];
            const targetData: PlanPriceDetails = (periodKey === 'monthly'
              ? targetPlan.monthlyData
              : targetPlan.annualData) as PlanPriceDetails;
            
            console.log(`✅ MAPPING SUCCESS: ${productIdentifier}`);
            console.log('   Target Plan:', planKey);
            console.log('   Target Period:', periodKey);
            console.log('   RevenueCat Price:', pkg.product.priceString);
            console.log('   UI Price (unchanged):', targetData.price);
            
            // Only assign the RevenueCat package for purchase flow
            // Keep UI prices as hardcoded display-only values
            targetData.rcPackage = pkg;
            
            console.log('   rcPackage assigned:', !!targetData.rcPackage);
          } else {
            console.error(`❌ MAPPING FAILED: ${productIdentifier}`);
            console.error('   planKey:', planKey, '(valid:', !!planKey, ')');
            console.error('   periodKey:', periodKey, '(valid:', !!periodKey, ')');
            console.error('   planExists:', planKey ? !!updatedPlans[planKey] : false);
          }
          
          console.log('-------------------');
        });

        setPlans(updatedPlans);
      } catch (err: any) {
        console.error('Failed to sync RevenueCat offerings:', err);
        
        // Log específico para erro de billing
        if (err?.message?.includes('BILLING_UNAVAILABLE')) {
          console.error('🚨 BILLING_UNAVAILABLE detected - using fallback plans');
        }
        
        // Sempre usar planos iniciais em caso de erro
        setPlans(initialPlans);
      } finally {
        setIsLoading(false);
      }
    };

    syncOfferings();
  }, []);

  if (isLoading && isNativeApp()) {
    return (
      <div className="flex justify-center items-center p-10">
        <p>Carregando planos...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-center mb-6">
        <div className="bg-gray-100 p-1 rounded-full flex items-center">
          <ToggleGroup type="single" value={billingPeriod} onValueChange={(value) => value && setBillingPeriod(value as "annual" | "monthly")}>
            <ToggleGroupItem value="monthly" aria-label="Mensal" className="rounded-full px-4 data-[state=on]:bg-white data-[state=on]:text-primary data-[state=on]:shadow-sm">
              Mensal
            </ToggleGroupItem>
            <ToggleGroupItem value="annual" aria-label="Anual" className="rounded-full px-4 data-[state=on]:bg-white data-[state=on]:text-primary data-[state=on]:shadow-sm">
              Anual
              <span className="ml-1.5 bg-red-100 text-red-600 text-xs font-medium px-1.5 py-0.5 rounded">-20%</span>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>
      
      {Object.entries(plans)
        .filter(([key, plan]) => {
          const isIOS = Capacitor.getPlatform() === 'ios';

          // Ocultar o plano Paixão no iOS
          if (isIOS && key === 'paixao') return false;

          // No web, mostrar todos os planos
          if (!isNativeApp()) return true;

          // No nativo, só mostrar planos que têm rcPackage para o período atual
          const currentData = billingPeriod === 'monthly' ? plan.monthlyData : plan.annualData;
          return 'rcPackage' in currentData && currentData.rcPackage != null;
        })

        .map(([key, plan]) => (
          <Plan
            key={key}
            {...plan}
            billingPeriod={billingPeriod}
            monthlyData={plan.monthlyData}
            annualData={plan.annualData}
          />
        ))}
    </>
  );
};

export default SubscriptionPlans;